import { useLocale } from '../Locale';
import FooterNav from '../components/FooterNav';
import BgVid from '../assets/videos/origami_bg.mp4';
import OrigamiSelectionStore from '../state/OrigamiSelectionStore';
import OrigamiDownloader from '../components/OrigamiDownloader';
import I18n from '../components/I18n';
import Link, { Button } from '../components/Link';
import { FacebookProvider, Share } from 'react-facebook';
import { view } from '@risingstack/react-easy-state';
import { useParams } from 'react-router';
import { colors, patterns, messages, foldedAssets } from '../origamiConfig';
import { useMemo, useCallback, useEffect } from 'react';
import AnalyticsContext from '../components/AnalyticsContext';
import { trackShare, trackPage } from '../adobeAnalytics';

const OrigamiShare = (props) => {
  const { color, pattern, message } = props;
  const locale = useLocale();

  const shareUrl = window.location.href;

  useEffect(() => {
  trackPage(`${locale}|origami-redeem-${color}-${pattern}-${message}`, 'viewer');
  }, [color, locale, message, pattern]);

  const { selectedColor, selectedPattern, selectedMessage, foldedImage } = useMemo(() => {
    return {
      selectedColor: colors.find((c) => c.id === color),
      selectedPattern: patterns.find((p) => p.id === pattern),
      selectedMessage: messages.find((m) => m.id === message),
      foldedImage: foldedAssets.find((a) => a.color === color && a.pattern === pattern)?.image,
    };
  }, [color, pattern, message]);

  const onFacebookShare = (originalFn) => {
    return (e, analytics) => {
      trackShare(analytics.componentName, shareUrl, 'share', 'intention', 'facebook');
      originalFn(e);
    };
  };

  const galleryUrl = useMemo(() => {
  const url = new URL(window.location.href);
    if (url.pathname.indexOf('/share') !== -1) {
      url.pathname = url.pathname.replace(/\/share.+/, '/index.html');
      url.hash = `#/${locale}/origami-gallery`;
      return url.toString();
    } else {
      return 'origami-gallery';
    }
  }, [locale]);

  const onTwitterShare = useCallback(
    (e, analytics) => {
      e.preventDefault();
      trackShare(analytics.componentName, shareUrl, 'share', 'intention', 'twitter');
      const text = encodeURIComponent(I18n.getTranslation(locale, 'origami.share.twitter'));
      let targetUrl = 'https://twitter.com/intent/tweet?text=' + text + '&hashtags=Nissan,Expo2020,OrigamiOfHope&url=' + encodeURIComponent(shareUrl);
      window.open(targetUrl, '_blank');
    },
    [locale, shareUrl],
  );

  if (process.env.NODE_ENV !== 'production') {
    window.getMeta = () => ({
      title: I18n.getTranslation(locale, `origami.subheadline`),
      description: I18n.getTranslation(locale, `origami.share.facebook`),
      'og:image': '',
      'og:url': '',
      'twitter:card': 'summary_large_image',
      'twitter:title': I18n.getTranslation(locale, `origami.subheadline`),
      'twitter:description': I18n.getTranslation(locale, `origami.share.facebook`),
      'twitter:image': '',
    });
  }

  const backUrl = useMemo(() => {
    const url = new URL(window.location.href);
    if (url.pathname.indexOf('/share') !== -1) {
      url.pathname = url.pathname.replace(/\/share.+/, '/index.html');
      url.hash = `#/${locale}/origami`;
      return url.toString();
    } else {
      return 'origami';
    }
  }, [locale]);

  useEffect(() => {
    if(locale === 'en') {
    window.modalComponent.init();
    }
  }, [locale]);

  return (
    <AnalyticsContext type="div" componentName="origamiShare" className={`container origami origami-share fullscreen lang-${locale}`}>
      <div className="bg-video">
        <video src={BgVid} playsInline muted autoPlay loop type="video/mp4" />
      </div>
      <div className={'content lang-' + locale}>
        <div className="page-logo lg nissan white">NISSAN</div>
        <div className={'origami-content'}>
          <div className={'origami-copy'}>
            <h1>
              {I18n.getTranslation(locale, 'origami.subheadline')}
            </h1>
            <h2>
              {I18n.getTranslation(locale, 'origami.shareHeadline')}
            </h2>
            <AnalyticsContext type="div" componentName="share" className="end-screen-share-buttons">
              <FacebookProvider appId="607295890692254">
                <Share href={shareUrl}>
                  {({ handleClick }) => (
                    <Button className="btn-share facebook" onClick={onFacebookShare(handleClick)} text="facebook share" />
                  )}
                </Share>
              </FacebookProvider>
              <Button className="btn-share twitter" onClick={onTwitterShare} text="twitter share" />
              <OrigamiDownloader selectedMessage={selectedMessage} foldedImage={foldedImage} />
            </AnalyticsContext>
            <Link href={galleryUrl} className="btn white" text={I18n.getTranslation(locale, 'origami.viewGallery')} newTab />
            { locale === 'en' ?
              <button className="btn white explore-nissan-cta">
                {I18n.getTranslation(locale, 'origami.exploreNissan.title')}
              </button> :
              <a href={I18n.getTranslation(locale, 'origami.exploreNissan.url')} className="btn white explore-nissan-cta" target="_blank">
                {I18n.getTranslation(locale, 'origami.exploreNissan.title')}
              </a> }
          </div>
          <div className={'origami-paper view-pattern'}>
            <div className={`origami-folded paper-${selectedColor.name} paper-${selectedPattern.name}`}></div>
            <p dangerouslySetInnerHTML={{ __html: I18n.getTranslation(locale, selectedMessage.text) }}></p>
            <p className="sm" dangerouslySetInnerHTML={{ __html: I18n.getTranslation(locale, selectedMessage.disclaimer) }}></p>
          </div>
        </div>
      </div>
      <FooterNav currentPage={'origami'} prev={{ text: 'general.back', url: backUrl }} />
    </AnalyticsContext>
  );
};

const OrigamiShareWrapper = view((props) => {
  const params = useParams();
  const store = OrigamiSelectionStore;

  const { color, pattern, message } = useMemo(() => {
    if (params.color != null && params.pattern != null && params.message != null) {
      return {
        color: Number(params.color),
        pattern: Number(params.pattern),
        message: Number(params.message),
      };
    }
    if (process.env.NODE_ENV !== 'production') {
      return {
        color: store.selectedColor.id,
        pattern: store.selectedPattern.id,
        message: store.selectedMessage.id,
      };
    }
    if (window.redemptionInfo) {
      return {
        color: window.redemptionInfo.color,
        pattern: window.redemptionInfo.pattern,
        message: window.redemptionInfo.message,
      };
    }
    throw new Error('Unable to determine color, pattern, message');
  }, [params.color, params.message, params.pattern, store.selectedColor.id, store.selectedMessage.id, store.selectedPattern.id]);

  return <OrigamiShare color={color} pattern={pattern} message={message} />;
});

export default OrigamiShareWrapper;
